import { DEFAULT_RISK_THRESHOLD_DAYS } from '../entities/Balance';
import type { RequestStatus } from '../vocabulary';
import { CalendarDate } from '../value-objects/CalendarDate';
import type { DateRange } from '../value-objects/DateRange';

/**
 * What a person still has, what they have committed, and what is about to be
 * lost.
 *
 * This is the calculation a Cloud Function runs to maintain `balances/{id}`, and
 * the same one the approval callable re-runs from source data before granting
 * days. Keeping it here — pure, with the requests passed in — means both callers
 * share one definition and the whole thing is testable without a database.
 *
 * The subtle rule: a request awaiting a decision consumes balance. Otherwise a
 * person could file three overlapping requests that each look affordable on its
 * own and end up approved for more days than they have.
 */

/** A request reduced to what the balance cares about. */
export interface BalanceContribution {
  readonly range: DateRange;
  readonly businessDays: number;
  readonly status: RequestStatus;
}

export interface BalanceInput {
  readonly entitledDays: number;
  readonly contributions: readonly BalanceContribution[];
  readonly today: CalendarDate;
  /** Days carried over from earlier periods that have not prescribed yet. */
  readonly carriedOverDays: number;
  /** When the carried-over days prescribe. */
  readonly carriedOverExpiresOn: CalendarDate | null;
  readonly riskThresholdDays: number;
}

export interface BalanceBreakdown {
  readonly entitledDays: number;
  /** Approved or closed days whose period has already passed. */
  readonly takenDays: number;
  /** Approved days still in the future. Committed, but not yet consumed. */
  readonly scheduledDays: number;
  /** Days on requests awaiting a decision. */
  readonly pendingDays: number;
  readonly remainingDays: number;
  readonly expiringDays: number;
  readonly expiresOn: CalendarDate | null;
  /** True when the accumulated balance is above the risk threshold. */
  readonly atRisk: boolean;
  /** True when days will prescribe and there is still time to book them. */
  readonly hasExpiryWarning: boolean;
  /** Calendar days until the expiry, or null. Drives the banner's countdown. */
  readonly daysUntilExpiry: number | null;
}

/** Statuses whose days are committed and therefore deducted. */
const COMMITTED_STATUSES: readonly RequestStatus[] = ['Aprobada', 'Tomada'];

/** Statuses still in flight — deducted too, see the note above. */
const IN_FLIGHT_STATUSES: readonly RequestStatus[] = [
  'Por revisar',
  'En revisión',
  'Visto bueno',
];

export const computeBalance = (input: BalanceInput): BalanceBreakdown => {
  let takenDays = 0;
  let scheduledDays = 0;
  let pendingDays = 0;

  for (const contribution of input.contributions) {
    if (COMMITTED_STATUSES.includes(contribution.status)) {
      // "Taken" is decided by the calendar, not by the status: an approved
      // request whose dates have passed is spent, whether or not HR has closed
      // it. Splitting these lets the profile show "10 tomados, 5 agendados".
      const alreadyPast = contribution.range.end.isBefore(input.today);
      if (alreadyPast || contribution.status === 'Tomada') {
        takenDays += contribution.businessDays;
      } else {
        scheduledDays += contribution.businessDays;
      }
      continue;
    }

    if (IN_FLIGHT_STATUSES.includes(contribution.status)) {
      pendingDays += contribution.businessDays;
    }
    // 'Rechazada', 'Cancelada', 'Borrador' and unknown statuses release their
    // days — an unknown status is treated as not consuming balance, which is the
    // safe direction for an older client reading a newer document.
  }

  const totalEntitlement = input.entitledDays + input.carriedOverDays;
  const remainingDays = Math.max(
    0,
    totalEntitlement - takenDays - scheduledDays - pendingDays,
  );

  // Carried-over days can only expire to the extent they are still unspent.
  const expiringDays =
    input.carriedOverExpiresOn === null
      ? 0
      : Math.max(0, Math.min(input.carriedOverDays, remainingDays));

  const daysUntilExpiry =
    input.carriedOverExpiresOn === null
      ? null
      : input.today.daysUntil(input.carriedOverExpiresOn);

  return {
    entitledDays: input.entitledDays,
    takenDays,
    scheduledDays,
    pendingDays,
    remainingDays,
    expiringDays,
    expiresOn: input.carriedOverExpiresOn,
    atRisk: remainingDays > input.riskThresholdDays,
    // Only warn while something can still be done about it. A banner for days
    // that prescribed last week is noise.
    hasExpiryWarning:
      expiringDays > 0 && daysUntilExpiry !== null && daysUntilExpiry > 0,
    daysUntilExpiry,
  };
};

export type AffordabilityError =
  | { readonly kind: 'insufficient'; readonly requested: number; readonly available: number }
  | { readonly kind: 'below-minimum'; readonly requested: number; readonly minimum: number }
  | { readonly kind: 'no-business-days' };

/**
 * Whether a person can afford a request. Checked at submission for a warning and
 * again inside the approval callable as the authoritative gate, where it is
 * recomputed from the stored requests rather than from the cached balance
 * document — a counter that lags must never be able to approve days that are not
 * there.
 */
export const canAfford = (params: {
  readonly requestedBusinessDays: number;
  readonly available: number;
  readonly minimumRequestDays: number;
}):
  | { readonly ok: true }
  | { readonly ok: false; readonly error: AffordabilityError } => {
  if (params.requestedBusinessDays <= 0) {
    return { ok: false, error: { kind: 'no-business-days' } };
  }
  if (params.requestedBusinessDays < params.minimumRequestDays) {
    return {
      ok: false,
      error: {
        kind: 'below-minimum',
        requested: params.requestedBusinessDays,
        minimum: params.minimumRequestDays,
      },
    };
  }
  if (params.requestedBusinessDays > params.available) {
    return {
      ok: false,
      error: {
        kind: 'insufficient',
        requested: params.requestedBusinessDays,
        available: params.available,
      },
    };
  }
  return { ok: true };
};

/**
 * When days accrued in a given year prescribe. Mexico's LFT gives 18 months from
 * the end of the accrual period, which is the default; the window is
 * configurable because company policy may be more generous.
 */
export const expiryDateFor = (
  accrualYear: number,
  expiryMonths: number,
): CalendarDate => CalendarDate.fromISOOrThrow(`${accrualYear}-12-31`).addMonths(expiryMonths);

export const isBalanceAtRisk = (
  remainingDays: number,
  threshold = DEFAULT_RISK_THRESHOLD_DAYS,
): boolean => remainingDays > threshold;
